import { create } from 'zustand';

import type { InstallPromptRequest, PullRequestPreview } from './types';

import { canShowPrompt, countToday, hasOptedOut, readHistory, wasShownThisWeek } from './schedule';

type InstallPromptStore = {
  request: InstallPromptRequest | null;
  /** Whether the request made it on screen, which used this week's prompt. */
  isShown: boolean;
  /** The signed-in person the prompts are for. */
  userId: string | null;
  clear: () => void;
};

export const useInstallPromptStore = create<InstallPromptStore>((set) => ({
  request: null,
  isShown: false,
  userId: null,
  clear: () => set({ request: null, isShown: false }),
}));

/**
 * Offers an install prompt for a moment that just happened. Dropped when another prompt is up or
 * the schedule says not now; the host then checks whether the person already has the app.
 */
export function offerInstallPrompt(request: InstallPromptRequest, now = Date.now()): void {
  const store = useInstallPromptStore.getState();
  if (store.request || !canShowPrompt(readHistory(now), request.id, now)) {
    return;
  }
  useInstallPromptStore.setState({ request, isShown: false });
}

/**
 * Prompts show the person's own pull requests, so signing in as someone else drops the one offered
 * to the last person.
 */
export function setInstallPromptUser(userId: string): void {
  const store = useInstallPromptStore.getState();
  if (store.userId === userId) {
    return;
  }
  useInstallPromptStore.setState(
    store.userId === null ? { userId } : { userId, request: null, isShown: false },
  );
}

/**
 * Whether another app promotion, such as the first-review desktop toast, may show now: no prompt is
 * up or waiting, none used this week's slot, and the person has not dismissed them for good. The first-day rule does not apply, since that toast
 * exists for someone's first review.
 */
export function canShowOtherAppPromotion(now = Date.now()): boolean {
  const history = readHistory(now);
  return (
    useInstallPromptStore.getState().request === null &&
    !wasShownThisWeek(history, now) &&
    !hasOptedOut(history)
  );
}

/** The second review submitted in a day offers review requests as desktop notifications. */
export function noteReviewSubmitted(pullRequest: PullRequestPreview): void {
  if (countToday('reviews') === 2) {
    offerInstallPrompt({ id: 'second-review', pullRequest });
  }
}

/** Copying a pull request's link offers Slack, which would unfurl it into a card. */
export function noteLinkCopied({
  owner,
  repo,
  number,
  title,
}: Omit<PullRequestPreview, 'title'> & { title: string | null }): void {
  const trimmedTitle = title?.trim();
  if (trimmedTitle) {
    offerInstallPrompt({
      id: 'copy-link',
      pullRequest: { owner, repo, number, title: trimmedTitle },
    });
  }
}
